import React from "react";
import { Flex, Box, AspectRatioBox, Text, Heading } from '@chakra-ui/core'
import NextLink from 'next/link'
import { ApiStory, getStorySlug, getPublicationDateForDisplay, getStoryAuthors, getStoryFeaturedImageUrl, getStorySummary, getStoryTitle, getStoryAuthor } from 'data/api/ApiStory'
import { getAuthorFullName, getAuthorSlug } from 'data/api/ApiAuthor'
import Image from 'next/image';

function Card({ children }) {
    return (
    <Flex bg="white" borderRadius={2} w="100%" border="1px solid" borderColor="gray.100" flexDirection="row" alignItems="center" position="relative">
        {children}
    </Flex>
    )
}

function StoryLink({ href }) {
    return (
      <NextLink href={href}>
        <a className="absolute inset-0 z-10" />
      </NextLink>
    );
}



function ImageContainer({ src, alt }) {
    return (
      <AspectRatioBox ratio={3 / 2} width="100%">
        <Box p={5} w="100%" h="100%" backgroundSize="cover" backgroundPosition="center" className="image-container">
          <Image src={`${src}?fm=jpg&fl=progressive&w=800`} layout="fill" className="next-image" alt={alt}/>
        </Box>
      </AspectRatioBox>
    );
}

function Content({ children }) {
    return (
        <Box p={5} w="100%">
            {children}
        </Box>
    )
}

function Date({ children }) {
    return (
      <Text fontSize="16" color="gray.600">
          {children}
      </Text>
    )
}

function AuthorsLink({ authors }) {
  return (
    authors.map((author, i) => {
      if(i == authors.length - 1){
        if(i == 1){
          return <AuthorLink authorSlug={author['slug']} connector=' and ' fullName={author['fullName']} />
        } else {
          return <AuthorLink authorSlug={author['slug']} connector=', and ' fullName={author['fullName']} />
        }
      } else if(i == 0) {
        return <AuthorLink authorSlug={author['slug']} connector='' fullName={author['fullName']} />
      } else {
        return <AuthorLink authorSlug={author['slug']} connector=', ' fullName={author['fullName']} />
      }
    })
  )
}

function AuthorLink({ authorSlug, connector, fullName }) {
  return (
    <>
      <a className="poppins font-medium text-lg relative z-20">
        {connector}
      </a>
      <NextLink href={`/author/${authorSlug}`}>
        <a className="orange poppins text-lg font-medium relative z-20 cursor-pointer hover:underline">
          {fullName}
        </a>
      </NextLink>
    </>
  );
}

function SingleAuthorLink({ author }){
  return (
    <NextLink href={`/author/${getAuthorSlug(author)}`}>
      <a className="orange poppins text-lg font-medium relative z-20 cursor-pointer hover:underline">
        {getAuthorFullName(author)}
      </a>
    </NextLink>
  );
}


function Title({ children }) {
    return (
      <Box>
        <Heading fontSize={22}>{children}</Heading>
      </Box>
    );
}

function Summary({ children }) {
    return (
      <Box py={1}>
        <Text fontSize={17}>
          {children}
        </Text>
      </Box>
    );
}

interface StoryCardProps {
    story: ApiStory
}

export function StoryCardCondensed(props: StoryCardProps) {
    const { story } = props
    return (
      <Card>
        <StoryLink href={`/${getStorySlug(story)}`}/>
        <Content>
          <Title>{getStoryTitle(story)}</Title>
          <Summary>
            {getStorySummary(story)}
          </Summary>
          {getStoryAuthors(story).length > 1 
          ? <AuthorsLink authors={getStoryAuthors(story)}/>
          : <SingleAuthorLink author={getStoryAuthor(story)} />}
          <Date>{getPublicationDateForDisplay(story)}</Date>
        </Content>
        <ImageContainer src={getStoryFeaturedImageUrl(story)} alt={getStoryTitle(story)} />
      </Card>
    );
}
